import { z } from 'zod';
import { t } from 'i18next';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import { peselSchema } from '@repo/shared/types';

import {
  CountryCode,
  getPostalRegex,
  POSTAL_CODE_FORMATS,
} from '../../welcome/loan-application-possible/liberis/common/helpers/countries';

const cityRegex = /^[\p{L}][\p{L}\s-]{1,98}[\p{L}]$/u; // must start and end with unicode letter, can contain unicode letters, spaces and hyphens
const streetRegex = /^[\p{L}0-9][\p{L}0-9\s\-\/\.]{1,98}[\p{L}0-9]$/u; // must start and end with unicode letter or number, can contain unicode letters, numbers, spaces, hyphens and forward slashes

const p24MarketingAgreements = z.object({
  selectAllCheckbox: z.boolean(),
  email: z.boolean(),
  sms: z.boolean(),
  tel: z.boolean(),
});

const addressSchema = z
  .object({
    street: z
      .string()
      .nonempty(t('validation.required'))
      .regex(
        streetRegex,
        t('welcomePage.loanApplicationPossible.liberis.validation.street.invalid'),
      )
      .min(2, t('welcomePage.loanApplicationPossible.liberis.validation.street.tooShort'))
      .max(100, t('welcomePage.loanApplicationPossible.liberis.validation.street.tooLong')),
    postCode: z.string().nonempty(t('validation.required')),
    city: z
      .string()
      .nonempty(t('validation.required'))
      .regex(cityRegex, t('welcomePage.loanApplicationPossible.liberis.validation.city')),
    country: z
      .string()
      .nonempty(t('validation.required'))
      .min(1, t('welcomePage.loanApplicationPossible.liberis.validation.country.tooShort')),
  })
  .superRefine((data, ctx) => {
    const countryValue = data.country as CountryCode;
    const regex = getPostalRegex(countryValue);

    if (!regex) {
      return;
    }

    if (!regex.test(data.postCode)) {
      const msg = `${t('welcomePage.loanApplicationPossible.liberis.validation.postCode')} ${POSTAL_CODE_FORMATS[countryValue].example}`;

      ctx.addIssue({
        code: 'custom',
        message: msg,
        path: ['postCode'],
      });
    }
  });

const representativeSchema = z.object({
  phone: z.string().nonempty(t('validation.required')),
  email: z
    .email({
      error: t('welcomePage.loanApplicationPossible.liberis.validation.mail.invalid'),
    })
    .nonempty(t('validation.required')),
  address: addressSchema,
});

const personsSchema = z
  .array(
    z
      .object({
        name: z.string(),
        isRepresentative: z.boolean(),
        isBeneficient: z.boolean(),
        shares: z.string().nonempty(t('validation.required')),
        pesel: peselSchema,
      })
      .extend(representativeSchema.shape),
  )
  .superRefine((data, ctx) => {
    // Email validation
    const emails = data.map((p) => p.email).filter((email) => email !== '');
    const uniqueEmails = new Set(emails);
    data.forEach((person, index) => {
      if (
        emails.length !== uniqueEmails.size &&
        uniqueEmails.has(person.email) &&
        emails.filter((email) => email === person.email).length > 1
      ) {
        ctx.addIssue({
          code: 'custom',
          message: t('welcomePage.loanApplicationPossible.liberis.validation.mail.duplicate'),
          path: [index, 'email'],
        });
      }
    });
  });

export type Persons = z.infer<typeof personsSchema>;

export const pragmaAgreementsSchema = z.object({
  pragmaAgreements: z.object({
    representative: z.boolean().refine((val) => val, {
      error: t('validation.required'),
    }),
    loanTerms: z.boolean().refine((val) => val, {
      error: t('validation.required'),
    }),
    pragmaTerms: z.boolean().refine((val) => val, {
      error: t('validation.required'),
    }),
    bikbig: z.boolean().refine((val) => val, {
      error: t('validation.required'),
    }),
    marketing: z.boolean(),
    payProDataProcessing: z.boolean().refine((val) => val, {
      error: t('validation.required'),
    }),
    pragmaGoDataProcessing: z.boolean().refine((val) => val, {
      error: t('validation.required'),
    }),
  }),
});

export const emptyPragmaAgreementsSchema = z.object({
  pragmaAgreements: z.object({
    representative: z.boolean(),
    loanTerms: z.boolean(),
    pragmaTerms: z.boolean(),
    bikbig: z.boolean(),
    marketing: z.boolean(),
    payProDataProcessing: z.boolean(),
    pragmaGoDataProcessing: z.boolean(),
  }),
});

export type PragmaAgreements = z.infer<typeof pragmaAgreementsSchema>;

const toYmdString = (v: unknown): string => {
  if (v == null) return '';

  if (dayjs.isDayjs(v as any)) {
    const d = v as Dayjs;

    return d.isValid() ? d.format('YYYY-MM-DD') : '';
  }

  if (v instanceof Date) {
    const d = dayjs(v);

    return d.isValid() ? d.format('YYYY-MM-DD') : '';
  }

  if (typeof v === 'string') {
    const s = v.trim();
    const m = s.match(/^(\d{4}-\d{2}-\d{2})(?:[ T]\d{2}:\d{2}:\d{2})?$/);
    if (m) return m[1];

    const d = dayjs(s);
    if (d.isValid()) return d.format('YYYY-MM-DD');

    return s;
  }

  return String(v);
};

const ymdStringSchema = z
  .string()
  // wymagane
  .min(1, {
    error: t('welcomePage.loanApplicationPossible.liberis.validation.companyRegisterDate.required'),
  })
  .refine((s) => /^\d{4}-\d{2}-\d{2}$/.test(s), {
    error: t(
      'welcomePage.loanApplicationPossible.liberis.validation.companyRegisterDate.invalidFormat',
    ),
  })
  .superRefine((s, ctx) => {
    const [yStr, mStr, dStr] = s.split('-');
    const year = Number(yStr);
    const month = Number(mStr);
    const day = Number(dStr);

    const mkUTC = (Y: number, M: number, D: number) => new Date(Date.UTC(Y, M - 1, D));
    const candidate = mkUTC(year, month, day);

    const minRealistic = mkUTC(1900, 1, 1);
    if (candidate < minRealistic) {
      ctx.addIssue({
        code: 'custom',
        message: t(
          'welcomePage.loanApplicationPossible.liberis.validation.companyRegisterDate.unrealisticDate',
        ),
      });

      return;
    }

    // 4) zakresy i realność daty
    const basicRangesOk =
      Number.isInteger(year) &&
      year >= 1900 &&
      year <= 2100 &&
      Number.isInteger(month) &&
      month >= 1 &&
      month <= 12 &&
      Number.isInteger(day) &&
      day >= 1 &&
      day <= 31;

    if (!basicRangesOk) {
      ctx.addIssue({
        code: 'custom',
        message: t(
          'welcomePage.loanApplicationPossible.liberis.validation.companyRegisterDate.invalidDate',
        ),
      });

      return;
    }

    if (
      candidate.getUTCFullYear() !== year ||
      candidate.getUTCMonth() + 1 !== month ||
      candidate.getUTCDate() !== day
    ) {
      ctx.addIssue({
        code: 'custom',
        message: t(
          'welcomePage.loanApplicationPossible.liberis.validation.companyRegisterDate.invalidDate',
        ),
      });

      return;
    }

    const now = new Date();
    const todayUTC = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
    if (candidate > todayUTC) {
      ctx.addIssue({
        code: 'custom',
        message: t(
          'welcomePage.loanApplicationPossible.liberis.validation.companyRegisterDate.featureDate',
        ),
      });
    }
  });

export const companyRegisterDateSchema = z.preprocess(toYmdString, ymdStringSchema);

export const requestedLoanFormSchema = z.object({
  amount: z.number(),
  merchantData: z.object({
    address: addressSchema,
  }),
  ibanId: z.string(),
  companyRegisterDate: companyRegisterDateSchema,
  person: personsSchema,
  agreements: z.object({
    bureau: z.boolean().refine((val) => val, {
      error: t('validation.required'),
    }),
    payProToLiberisData: z.boolean().refine((val) => val, {
      error: t('validation.required'),
    }),
    liberisDataToPayPro: z.boolean().refine((val) => val, {
      error: t('validation.required'),
    }),
    liberisMarketing: z.boolean(),
  }),
});

export type RequestedLoanValues = z.infer<typeof requestedLoanFormSchema> & PragmaAgreements;

export const requestedLoanFormSchemaWithP24Agreements = requestedLoanFormSchema.extend(
  p24MarketingAgreements.shape,
);

export type RequestedLoanValuesWithP24Agreements = z.infer<
  typeof requestedLoanFormSchemaWithP24Agreements
> &
  PragmaAgreements;
